import * as React from 'react';
import {
  ListingWithReps,
  OfferSettingsJsonType,
  AgentGroup,
  OfferActionNotifiedType,
  ListingOfferNotificationsDTO,
  JsonObject,
} from '@bb/domain/dist/types';
import moment from 'moment';
import { Table } from 'react-bootstrap';
import { formatExactTimestamp, formatPhoneNumber } from 'utils';
import { OfferDoc } from '@bb/domain/dist/schemas';

interface OfferListTableProps {
  listing: ListingWithReps;
  offers: JsonObject<OfferDoc>[];
  offerSettings: OfferSettingsJsonType;
  offerActions: ListingOfferNotificationsDTO;
  openOfferPreview: (offerId: string) => void;
  showStatus?: boolean;
  showActions?: boolean;
  showRegistered?: boolean;
  openForm801Modal: (listing: ListingWithReps, offer: unknown) => void;
  openOfferNotificationModal: (listing: ListingWithReps) => void;
}

interface ContentMapItem {
  label: string;
  classes: string;
  context: React.ReactNode;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}
interface ContentMap {
  [key: string]: ContentMapItem;
}

const AGENT_GROUP_LABELS: Record<AgentGroup, string> = {
  all: 'All Showing Agents',
  registered: 'Agents with Registered Offers',
  none: 'Listing Agent Only',
  selected: 'Selected Agents Only',
};

const ACTION_AGENT_GROUP_LABELS: Record<OfferActionNotifiedType, string> = {
  notifiedAll: 'All Showing Agents',
  notifiedRegistered: 'Agents with Registered Offers',
  notifiedListingAgent: 'Listing Agent Only',
  notifiedSelected: 'Selected Agents Only',
};

const OfferListTable: React.FC<OfferListTableProps> = ({
  listing,
  offers,
  offerSettings,
  offerActions,
  openOfferPreview,
  showActions,
  showStatus,
  showRegistered,
  openForm801Modal,
  openOfferNotificationModal,
}) => {
  const COLUMNS: { label: string; style?: React.CSSProperties }[] = [
    {
      label: '',
    },
    {
      label: 'Agent Name',
    },
    {
      label: 'Contact Info',
    },
    {
      label: 'Date Registered',
    },
    {
      label: 'Irrevocable',
    },
    {
      label: 'Offer Doc',
      style: { textAlign: 'center' as const },
    },
    showStatus && showRegistered
      ? {
          label: 'Status',
          style: { textAlign: 'center' as const },
        }
      : undefined,
    showActions
      ? {
          label: '',
        }
      : undefined,
  ].filter(column => column !== undefined);

  const manageNotificationIndex =
    offers.length - offers.filter(({ isVerified }) => isVerified).length;

  return (
    <Table className="offer-list-table" striped bordered hover responsive>
      <thead>
        <tr>
          {COLUMNS.filter(c => c).map(({ label, style }, index) => (
            <th key={index} style={style}>
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {offers
          .sort((a, b) => {
            let aScore = 0;
            let bScore = 0;
            if (a.isPendingNotification) aScore = 1;
            if (b.isPendingNotification) bScore = 1;
            if (!a.isVerified) aScore = 2;
            if (!b.isVerified) bScore = 2;
            if (bScore === aScore && !a.isVerified) {
              return moment(b.createdAt).unix() - moment(a.createdAt).unix();
            }
            return (
              bScore - aScore ||
              moment(b.verifiedAt).unix() - moment(a.verifiedAt).unix()
            );
          })
          .map((offer, offerIndex) => {
            const {
              attachmentForm801,
              isPendingNotification,
              isVerified,
              id,
              coop,
              irrevocable,
              createdAt,
              createdBy,
            } = offer;

            const offerPreviewCell = (
              <td key="offer-preview" className="text-center">
                <button
                  className="btn btn-default"
                  onClick={() => openOfferPreview(id)}
                >
                  <i className="pe-7s-look fat"></i>
                </button>
              </td>
            );
            const agentNameCell = (
              <td key="agent-name">
                <div>{coop.name}</div>
                <div style={{ fontWeight: 'bold' }}>{coop.org.name}</div>
              </td>
            );
            const contactInfoCell = (
              <td key="contact-info">
                <div>{coop.email}</div>
                <div>m: {formatPhoneNumber(coop.phone)}</div>
                <div>o: {formatPhoneNumber(coop.org.phone)}</div>
              </td>
            );
            const dateRegisteredCell = (
              <td key="date-registered">
                {formatExactTimestamp(createdAt, listing.timezone)}
                <br />
                By: {createdBy || 'Unknown'}
              </td>
            );
            const irrevocableCell = (
              <td key="irrevocable">
                {irrevocable ? (
                  <>
                    {moment(irrevocable).format('MMM, D YYYY')}
                    <br />
                    {moment(irrevocable).format('h:mm A')}
                  </>
                ) : (
                  'Unknown'
                )}
              </td>
            );
            const form801Cell = (
              <td key="form-801" className="text-center">
                {attachmentForm801 ? (
                  <a
                    style={{ fontSize: '22px' }}
                    href={attachmentForm801.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fa fa-file-pdf-o"></i>
                  </a>
                ) : (
                  <label style={{ color: 'red' }}>none</label>
                )}
              </td>
            );
            const statusCell = () => {
              const contentMap: ContentMap = {
                manage: {
                  label: 'Manage Notification',
                  classes: 'btn-success',
                  context: <small>Pending Notification Approval</small>,
                  // TODO: Add onClick
                  onClick: (
                    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                  ) => {
                    openOfferNotificationModal(listing);
                  },
                },
                verify: {
                  label: 'Verify Offer Doc',
                  classes: 'btn-warning',
                  context: (
                    <>
                      <small>Offer Registered Online</small>
                      <br />
                      <small>Verify Offer Doc for accuracy</small>
                    </>
                  ),
                  onClick: (
                    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                  ) => {
                    openForm801Modal(listing, offer);
                  },
                },
              };
              const content = () => {
                let data: ContentMapItem | undefined;
                if (!isVerified) {
                  data = contentMap.verify;
                } else if (
                  isPendingNotification &&
                  manageNotificationIndex === offerIndex
                ) {
                  data = contentMap.manage;
                }

                if (data) {
                  return (
                    <>
                      <div style={{ width: '100%' }}>
                        <button
                          className={`btn btn-sm ${data.classes}`}
                          onClick={data.onClick}
                          data-cy={`offer-${data.label
                            ?.toLowerCase()
                            .replace(' ', '-')}`}
                        >
                          {data.label}
                        </button>
                      </div>
                      <div style={{ width: '100%' }}>{data.context}</div>
                    </>
                  );
                }

                // Default
                return (
                  <div style={{ width: '100%' }}>
                    <div>
                      <strong>
                        <small>Notification Sent To:</small>
                      </strong>
                    </div>
                    <div>
                      <strong>
                        <small>
                          {offerActions[id]
                            ? ACTION_AGENT_GROUP_LABELS[offerActions[id].action]
                            : AGENT_GROUP_LABELS[offerSettings.agentGroup]}
                        </small>
                      </strong>
                    </div>
                  </div>
                );
              };
              const wrapper = (
                <td key="action" className="text-center">
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                    }}
                  >
                    {content()}
                  </div>
                </td>
              );

              return wrapper;
            };
            const actionCell = (
              <td key="manage">
                <a
                  href={`#/listing/${listing.id}/offer/${id}`}
                  className="btn btn-sm btn-info"
                  data-cy={`manage-offer-${id}`}
                >
                  Manage
                </a>
              </td>
            );
            const CELLS: (JSX.Element | false | undefined)[] = [
              offerPreviewCell,
              agentNameCell,
              contactInfoCell,
              dateRegisteredCell,
              irrevocableCell,
              form801Cell,
              showStatus && showRegistered && statusCell(),
              showActions && actionCell,
            ];
            return <tr key={id}>{CELLS}</tr>;
          })}
      </tbody>
    </Table>
  );
};

export default OfferListTable;
